import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Loader2,
  PlayCircle,
  Info,
  Filter,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/components/ui/use-toast';
import { METRIC_STATUS_META } from '@/pages/monitoring/components/SeverityBadge';
import {
  fetchMetricsHealth,
  runMonthlyEvaluation,
} from '@/pages/monitoring/services/monitoring.services';

const monthLabel = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const CURRENT_MONTH = monthLabel(new Date());

const lastClosedMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return monthLabel(d);
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const YESTERDAY = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toISODate(d);
})();

const FIRST_OF_MONTH = (() => {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
})();

const dec2 = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const cop = (v) => `$${Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;

const fmtValor = (valor, unidad) => {
  if (valor === null || valor === undefined) return '—';
  if (unidad === 'COP/kg') return cop(valor);
  if (unidad === '%') return `${dec2(valor)}%`;
  return `${dec2(valor)} ${unidad}`;
};

/**
 * Matriz centro x métrica con semáforo contra los parámetros configurados en
 * cada centro (Laundry.indicadores).
 *
 * El control se hace SIEMPRE sobre los datos sin consolidar: el backend agrega
 * los registros operativos del período (misma fuente que el Reporte 360), no
 * los KPIs de los cortes mensuales. Por eso admite el mes en curso y no espera
 * al cierre. La contrapartida es el costo: ~20 s para los 18 centros.
 */
const MetricsHealthTab = ({ onNotificationsChanged }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [modo, setModo] = useState('mes');
  const [periodo, setPeriodo] = useState(CURRENT_MONTH);
  const [from, setFrom] = useState(FIRST_OF_MONTH);
  const [to, setTo] = useState(YESTERDAY);

  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [soloFueraDeRango, setSoloFueraDeRango] = useState(false);
  // Evaluar los 18 centros tarda ~20 s; sin este guardia el doble render de
  // StrictMode (y el doble clic) lanzan dos veces la misma carga sobre Mongo.
  const enVueloRef = useRef(false);

  const load = useCallback(async () => {
    if (enVueloRef.current) return;
    enVueloRef.current = true;
    setLoading(true);
    try {
      const params =
        modo === 'mes'
          ? (() => {
              const [y, m] = periodo.split('-').map(Number);
              return { year: y, month: m };
            })()
          : { from, to };
      const resp = await fetchMetricsHealth(params);
      if (!resp?.state) throw new Error(resp?.message || 'Respuesta inválida');
      setData(resp.data);
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudo cargar la salud de métricas.',
        variant: 'destructive',
      });
    } finally {
      enVueloRef.current = false;
      setLoading(false);
    }
  }, [modo, periodo, from, to, toast]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, periodo]);

  const handleGenerarAlertas = async () => {
    const [y, m] = periodo.split('-').map(Number);
    setRunning(true);
    try {
      const resp = await runMonthlyEvaluation({ year: y, month: m });
      const nuevas = resp?.data?.notificacionesNuevas ?? 0;
      const cerradas = resp?.data?.autoResueltas ?? 0;
      const partes = [];
      if (nuevas > 0) partes.push(`${nuevas} nueva(s)`);
      if (cerradas > 0) partes.push(`${cerradas} cerrada(s) por corrección`);
      toast({
        title: `Evaluación de ${periodo}`,
        description: partes.length
          ? `${partes.join(' · ')}.`
          : 'Sin cambios: los hallazgos existentes no se duplican.',
      });
      onNotificationsChanged?.();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudo ejecutar la evaluación.',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  const reglas = data?.reglas || [];
  
  const rows = useMemo(() => {
    const all = data?.laundries || [];
    if (!soloFueraDeRango) return all;
    return all.filter((r) => r.resumen.critico > 0 || r.resumen.alerta > 0);
  }, [data, soloFueraDeRango]);

  const totales = useMemo(() => {
    const all = data?.laundries || [];
    return all.reduce(
      (acc, r) => ({
        critico: acc.critico + (r.resumen?.critico || 0),
        alerta: acc.alerta + (r.resumen?.alerta || 0),
        ok: acc.ok + (r.resumen?.ok || 0),
        sinMeta: acc.sinMeta + (r.resumen?.sinMeta || 0),
      }),
      { critico: 0, alerta: 0, ok: 0, sinMeta: 0 },
    );
  }, [data]);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setModo('mes')}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                  modo === 'mes'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                }`}
              >
                Mes
              </button>
              <button
                type="button"
                onClick={() => setModo('rango')}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                  modo === 'rango'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                }`}
              >
                Rango libre
              </button>
            </div>

            {modo === 'mes' ? (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">Período</Label>
                <input
                  type="month"
                  value={periodo}
                  max={CURRENT_MONTH}
                  onChange={(e) => setPeriodo(e.target.value)}
                  className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/20"
                />
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs text-muted-foreground">Desde</Label>
                  <Input
                    type="date"
                    value={from}
                    className="w-40"
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs text-muted-foreground">Hasta</Label>
                  <Input
                    type="date"
                    value={to}
                    className="w-40"
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
                <Button size="sm" onClick={load} disabled={loading} className="h-10">
                  {loading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Calcular
                </Button>
              </>
            )}

            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <Button
                variant={soloFueraDeRango ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSoloFueraDeRango((v) => !v)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Solo con desviación
              </Button>
              {modo === 'mes' && (
                <>
                  <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                  <Button size="sm" onClick={handleGenerarAlertas} disabled={running || loading}>
                    {running ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <PlayCircle className="mr-2 h-4 w-4" />
                    )}
                    Generar alertas de {periodo}
                  </Button>
                </>
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 mt-px shrink-0" />
            <span>
              Los valores se agregan directamente de los registros del período (recepciones,
              ciclos, servicios, inventarios y horas), no de los consolidados mensuales: el
              control aplica desde el primer día del mes. La primera consulta de un período
              recorre los 18 centros y puede tardar cerca de un minuto; luego queda en caché
              unos minutos y responde al instante.
              {data?.periodo?.parcial && (
                <strong className="font-medium text-foreground">
                  {' '}Mes en curso: datos hasta el {data.periodo.hasta}.
                </strong>
              )}
            </span>
          </p>

          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant="destructive" className="gap-1">
              {totales.critico} fuera de rango
            </Badge>
            <Badge variant="warning" className="gap-1">
              {totales.alerta} en tolerancia
            </Badge>
            <Badge variant="success" className="gap-1">
              {totales.ok} en parámetro
            </Badge>
            <Badge variant="outline" className="gap-1 text-muted-foreground">
              {totales.sinMeta} sin meta configurada
            </Badge>
          </div>
        </CardContent>
      </Card>


      {/* Matriz centro x métrica */}
      <Card>
        <CardContent className="pt-4">
          {loading && !data ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-3 text-sm text-muted-foreground">Evaluando métricas…</span>
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">
              {soloFueraDeRango
                ? 'Ningún centro presenta desviaciones en este período.'
                : 'No hay datos de métricas para este período.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide w-52 sticky left-0 bg-card">
                      Centro de lavado
                    </th>
                    {reglas.map((r) => (
                      <th
                        key={r.key}
                        className="text-center py-2.5 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wide min-w-[104px]"
                        title={`${r.label} (${r.unidad}) — ${
                          r.direccion === 'lower' ? 'menor es mejor' : 'mayor es mejor'
                        }`}
                      >
                        {r.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {rows.map((row, idx) => (
                    <motion.tr
                      key={row.laundryId}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(idx * 0.02, 0.4) }}
                      className="hover:bg-muted/20 transition-colors"
                    >
                      <td className="py-2.5 px-4 sticky left-0 bg-card">
                        <button
                          type="button"
                          onClick={() => navigate('/dashboard/eficiencia')}
                          className="text-left group"
                          title="Abrir el Reporte 360"
                        >
                          <span className="font-medium text-foreground group-hover:underline">
                            {row.nombre}
                          </span>
                        </button>
                        <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                          {row.origen === 'sin_datos'
                            ? 'sin consolidado'
                            : `${Math.round(row.kilosProcesados).toLocaleString('es-CO')} kg`}
                        </p>
                      </td>
                      {reglas.map((r) => {
                        const m = (row.metricas || []).find((x) => x.key === r.key);
                        if (!m) {
                          return (
                            <td key={r.key} className="py-2.5 px-2 text-center">
                              <span className="text-xs text-muted-foreground/40">—</span>
                            </td>
                          );
                        }
                        const meta = METRIC_STATUS_META[m.estado] || METRIC_STATUS_META.sin_datos;
                        return (
                          <td key={r.key} className="py-2.5 px-2 text-center">
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`w-full rounded-md border px-2 py-1.5 text-xs font-medium tabular-nums transition-transform hover:scale-[1.03] ${meta.className}`}
                                >
                                  {m.valorReal === null ? '—' : dec2(m.valorReal)}
                                  {m.desviacionPct !== null &&
                                    (m.estado === 'alerta' || m.estado === 'critico') && (
                                      <span className="block text-[10px] font-normal opacity-80">
                                        {m.desviacionPct > 0 ? '+' : ''}
                                        {m.desviacionPct.toFixed(0)}%
                                      </span>
                                    )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72">
                                <p className="text-sm font-semibold">{m.label}</p>
                                <p className="text-xs text-muted-foreground mb-3">
                                  {row.nombre} · {data?.periodo?.label}
                                </p>
                                <dl className="space-y-1.5 text-xs">
                                  <div className="flex justify-between gap-4">
                                    <dt className="text-muted-foreground">Estado</dt>
                                    <dd className="font-medium">{meta.label}</dd>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <dt className="text-muted-foreground">Valor real</dt>
                                    <dd className="font-medium tabular-nums">
                                      {fmtValor(m.valorReal, m.unidad)}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <dt className="text-muted-foreground">
                                      {m.direccion === 'lower' ? 'Máximo' : 'Mínimo'} esperado
                                    </dt>
                                    <dd className="font-medium tabular-nums">
                                      {m.valorEsperado > 0
                                        ? fmtValor(m.valorEsperado, m.unidad)
                                        : 'no configurado'}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <dt className="text-muted-foreground">Tolerancia</dt>
                                    <dd className="font-medium tabular-nums">
                                      {m.toleranciaPct}%
                                    </dd>
                                  </div>
                                  <div className="flex justify-between gap-4">
                                    <dt className="text-muted-foreground">Desviación</dt>
                                    <dd className="font-medium tabular-nums">
                                      {m.desviacionPct === null
                                        ? '—'
                                        : `${m.desviacionPct > 0 ? '+' : ''}${m.desviacionPct.toFixed(1)}%`}
                                    </dd>
                                  </div>
                                </dl>
                                {m.estado === 'sin_meta' && (
                                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                                    Configura la meta en Parametrización → Centros de Lavado →
                                    Indicadores.
                                  </p>
                                )}
                              </PopoverContent>
                            </Popover>
                          </td>
                        );
                      })}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MetricsHealthTab;
